import React, {
    useContext, useMemo, useEffect, useState, useCallback,
} from 'react';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import apolloClient from 'app/apollo/client';

import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import { useSelectedFilters } from 'app/connect/connectToSelectedFilters';
import {
    allDishesListVar,
    menuDishesIdListVar,
    selectedPeriodVar,
    regionSubdivisionVar,
} from 'app/apollo/reaction';

import { CONTRY_SUBDIVISION_FRAGMENT } from 'app/graphql/network/fragments/address.fragment';
import ROOT_MENU_DISHES_LIST from 'app/graphql/network/menuDishes2';

import {
    DEFAULT_CATEGORIES,
} from 'app/const/categories';
import {
    selectDishes as selectDishesByFilter,
    getDishesFromBasketSections,
} from 'app/utils/dish';
import { useBasketQueryLocate } from 'app/hooks/useBasketQueryLocate';
import { useMenuPeriodsData } from 'app/hooks/accessors';
import { sleep } from 'global/utils';
import { abTestDataContext } from '../../../containers/contexts/abTest.context';
import { ONE_DAYS_IN_UTC_FORMAT } from '../../../containers/contexts/menuDates.consts';

const getCityCtxDateExtender = () => {
    const addressSession = apolloClient.readFragment({
        id: 'address:session_address', fragment: CONTRY_SUBDIVISION_FRAGMENT,
    });
    if (!addressSession) {
        return 0;
    }
    const { country_subdivision } = addressSession;
    return ['RU-MOS', 'RU-MOW'].includes(country_subdivision) ? 0 : ONE_DAYS_IN_UTC_FORMAT;
};

function getDishesQueryVariables(selectedFilters, menuFilter) {
    const { selectedPeriod } = selectedFilters;
    const period = menuFilter?.periods?.find((p) => p.start === selectedPeriod);
    return {
        period: selectedPeriod,
        periodObj: period,
        tags: period?.tags?.map((tag) => tag.id) || [],
    };
}

function selectMenuDishes(
    allDishes,
    basketDishes,
    tags,
    categoryIds,
    eeAvailableDates,
    dateFilterStatus,
) {
    const cityCtxDateExtender = getCityCtxDateExtender();

    return {
        menuDishes: selectDishesByFilter(
            allDishes,
            null,
            tags,
            categoryIds,
            basketDishes,
            eeAvailableDates,
            dateFilterStatus,
            cityCtxDateExtender,
        ),
        filtredDishes: selectDishesByFilter(allDishes, null, tags, categoryIds),
        allDishes,
    };
}

const applyFilters = ({ menuDishes }, period) => menuDishes.filter((d) => {
    const dishTagIds = d.tags ? d.tags.map((t) => t.id) : [];
    if (!period) return false;

    if (period.excludeTags) {
        const dishHasExcludeTags = dishTagIds.find((dishTag) => period.excludeTags.includes(dishTag));
        return !dishHasExcludeTags;
    }
    if (period.includeTagsOnly) {
        const dishHasIncludeTags = dishTagIds.find((dishTag) => period.includeTagsOnly.includes(dishTag));
        return dishHasIncludeTags;
    }

    return true;
});

export function useMenuDishes(options = {}) {
    const {
        selectedPeriod: externalSelectedPeriod,
        fetchPolicy = 'cache-first',
        autoFetch = true,
    } = options;

    const appPeriod = useReactiveVar(selectedPeriodVar);
    const subdivision = useReactiveVar(regionSubdivisionVar);

    const [basketQuery] = useBasketQueryLocate();
    const selectedFilters = useSelectedFilters();
    const menuFilter = useMenuPeriodsData();

    const [dishesData, setDishesData] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [localLoading, setLocalLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    const sections = useMemo(
        () => basketQuery?.cart?.sections ?? [],
        [basketQuery],
    );

    const filters = externalSelectedPeriod
        ? { selectedPeriod: externalSelectedPeriod, selectedCategories: DEFAULT_CATEGORIES, selectedTags: [] }
        : selectedFilters;

    const { tags, periodObj: period } = getDishesQueryVariables(filters, menuFilter);

    const hash = useMemo(
        () => `${appPeriod}:${subdivision}`,
        [appPeriod, subdivision],
    );

    const [fetchMenuDishes] = useLazyQuery(ROOT_MENU_DISHES_LIST, {
        context: {
            message: 'root:init:useMenuDishes',
        },
    });

    const tagsKey = useMemo(() => tags.join(','), [tags]);

    const periodStart = useMemo(() => period?.start, [period]);

    const loadDishes = useCallback(async () => {
        setLocalLoading(true);
        setIsUpdating(true);

        try {
            const { data } = await fetchMenuDishes({
                variables: {
                    period: appPeriod,
                    tags,
                },
                fetchPolicy,
            });

            const dishes = applyFilters(data, period);
            setDishesData(dishes);
            allDishesListVar(dishes);
            setHasLoaded(true); // ← помечаем, что загрузили

            await sleep(600);
        } catch (error) {
            console.error('Error loading dishes:', error);
        } finally {
            setIsUpdating(false);
            setLocalLoading(false);
        }
    }, [fetchMenuDishes, appPeriod, tagsKey, periodStart, fetchPolicy]);

    useEffect(() => {
        if (autoFetch && !hasLoaded) {
            loadDishes();
        }
    }, [hash, autoFetch, loadDishes, hasLoaded]);

    useEffect(() => {
        setHasLoaded(false);
    }, [hash]);

    const {
        state: {
            eeAvailableDates,
            dateFilterStatus,
            filterPeriod: menuDatesStateFilterPeriod,
        },
    } = useContext(menuDatesState);

    const basketDishes = useMemo(() => getDishesFromBasketSections(sections), [sections]);
    const { selectedTags, selectedCategories } = filters;

    const result = useMemo(() => {
        let responseDishes;

        if (menuDatesStateFilterPeriod !== filters.selectedPeriod) {
            responseDishes = selectMenuDishes(
                dishesData, basketDishes, selectedTags, selectedCategories, undefined, undefined,
            );
        } else {
            responseDishes = selectMenuDishes(
                dishesData, basketDishes, selectedTags, selectedCategories, eeAvailableDates, dateFilterStatus,
            );
        }

        return responseDishes;
    }, [
        dishesData,
        basketDishes,
        selectedTags,
        selectedCategories,
        eeAvailableDates,
        dateFilterStatus,
        menuDatesStateFilterPeriod,
        filters.selectedPeriod,
    ]);

    const { menuDishes, allDishes, filtredDishes } = result;

    useEffect(() => {
        const basket_IDs = basketDishes?.map((itm) => itm.id) || [];
        const menu_IDs = menuDishes?.map((itm) => itm.id) || [];
        menuDishesIdListVar([...basket_IDs, ...menu_IDs]);
    }, [menuDishes, basketDishes]);

    const field = menuDishes?.length ? 'menuDishes' : 'filtredDishes';

    const filteredMenuDishes = useMemo(
        () => result[field]?.filter((d) => d.id !== '5332') || [],
        [result, field],
    );

    const loading = localLoading || isUpdating;

    return useMemo(() => ({
        loading,
        menuDishes: filteredMenuDishes,
        allDishes: allDishes || [],
        filtredDishes: filtredDishes || [],
        basketDishes: basketDishes || [],
        refetch: loadDishes,
        hasDataForRenderDishes: dishesData.length > 0,
        selectedFilters: filters,
    }), [
        loading,
        filteredMenuDishes,
        allDishes,
        filtredDishes,
        basketDishes,
        loadDishes,
        dishesData.length,
        filters,
    ]);
}
